import { RightOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Collapse,
  Empty,
  Input,
  Modal,
  Table,
  Typography,
  Checkbox,
  Flex,
} from 'antd';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CloseIconsForModalWithDataTestId,
  EmptyComponentWithDataTestId,
  MultipleSelect,
  MassActionsDropdown,
} from '@app/components';
import {
  ADD_CLIENT_SERVICES_LABEL,
  ADD_SERVICE_PATH,
  CANCEL,
  EMPLOYEES_CLIENTS_SERVICES_HEADER,
  PREFIX,
  SAVE,
} from '@app/constants';
import { useUsersAccessRights } from '@app/hooks';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import type { NEmployees } from '@app/types/Employees';

import { useClientServicesTab } from '@pages/Employees/EditEmployee/components/ClientServicesTab/hooks/useClientServicesTab';

import {
  CopyEmployeeServicesModal,
  SetEmployeeSalaryModal,
} from '@pages/Employees/EditEmployee/components/ClientServicesTab/components';
import { adaptDataToTable } from '@pages/Employees/EditEmployee/components/ClientServicesTab/utils/adaptDataToTable';

import '@pages/Employees/EditEmployee/components/ClientServicesTab/styles/ClientServicesTab.desktop.scss';
import '@pages/Employees/EditEmployee/components/ClientServicesTab/styles/ClientServicesTab.tablet.scss';

const { Text, Paragraph } = Typography;

export const ClientServicesTab = (
  data: NEmployees.EmployeeClientServicesTab
) => {
  const navigate = useNavigate();
  const {
    employee,
    allServices,
    setAllServices,
    setNewData,
    getEmployeeData,
    servicesLoadState,
  } = data;

  const {
    servicesToShow,
    isLoading,
    allCategories,
    selectedCategories,
    setSelectedCategories,
    deleteCategory,
    modalOpen,
    setModalOpen,
    handleOk,
    onHourSelect,
    onMinuteSelect,
    transformMinutes,
    processedModalServices,
    handleServiceModalCheckboxClick,
    serviceLabelClick,
    addingFromModalServices,
    saveData,
    changedServices,
    handleModalClose,
    onSalaryTypeSelect,
    onSalaryValueChange,
    handleAddServicesSalaryChange,
    employeeSalaryModalOpen,
    setEmployeeSalaryModalOpen,
    isCopyModalOpen,
    setIsCopyModalOpen,
    isCopyingServices,
    isSavingBlocked,
    finishCopyingServices,
  } = useClientServicesTab(
    employee,
    allServices,
    setAllServices,
    setNewData,
    getEmployeeData,
    servicesLoadState
  );

  const { hasUserRightToDeleteService, hasUserRightToCreateService } =
    useUsersAccessRights();

  const [searchQuery, setSearchQuery] = useState('');

  const items = useMemo(
    () => [
      {
        key: '1',
        disabled: isSavingBlocked,
        label: (
          <div
            onClick={() => {
              setEmployeeSalaryModalOpen(true);
            }}
          >
            Установить заработную плату
          </div>
        ),
      },
    ],
    [isSavingBlocked, setEmployeeSalaryModalOpen]
  );

  return (
    <div className={`${PREFIX}clients-services`}>
      {hasUserRightToCreateService && (
        <Flex justify="space-between" align="center" gap={16}>
          <Paragraph className="mg-0">
            Скопируйте услуги исполнителя вместе с длительностью и заработной
            платой другому исполнителю.
          </Paragraph>
          <Button
            type="primary"
            onClick={() => setIsCopyModalOpen(true)}
            disabled={
              !allServices.length || isCopyingServices || isSavingBlocked
            }
            data-testid="copy-services"
          >
            Копировать
          </Button>
        </Flex>
      )}

      <Flex justify="space-between" align="center">
        <MultipleSelect
          className="user-selector"
          placeholder="Все категории"
          allOptions={allCategories.map((category) => {
            return {
              label: category.Name,
              value: category.Id,
            };
          })}
          width={100}
          selectedOption={selectedCategories}
          setSelectedOptions={setSelectedCategories}
          dataTestId="all-categories-select"
        />
        <MassActionsDropdown items={items} data={servicesToShow} />
      </Flex>

      {servicesLoadState.status === 'error' && (
        <Alert
          type="error"
          showIcon
          message={
            isCopyingServices
              ? 'Не удалось загрузить услуги для копирования. Сохранение недоступно — отмените копирование или запустите его заново.'
              : 'Не удалось загрузить услуги исполнителя. Сохранение недоступно — повторите загрузку.'
          }
          action={
            <Button
              size="small"
              onClick={
                isCopyingServices ? finishCopyingServices : getEmployeeData
              }
            >
              {isCopyingServices ? 'Отменить копирование' : 'Повторить'}
            </Button>
          }
        />
      )}

      {servicesLoadState.status === 'success' && isCopyingServices && (
        <Alert
          type="info"
          showIcon
          message="Услуги скопированы у другого исполнителя. Проверьте их и нажмите «Сохранить» — текущие услуги исполнителя будут заменены."
          action={
            <Button size="small" onClick={finishCopyingServices}>
              Отменить копирование
            </Button>
          }
        />
      )}

      <Table
        locale={{ emptyText: <EmptyComponentWithDataTestId /> }}
        columns={EMPLOYEES_CLIENTS_SERVICES_HEADER}
        loading={isLoading || servicesLoadState.status === 'loading'}
        pagination={false}
        rowKey={(record) => record.Key}
        dataSource={adaptDataToTable(
          servicesToShow,
          deleteCategory,
          onHourSelect,
          onMinuteSelect,
          hasUserRightToDeleteService,
          onSalaryTypeSelect,
          onSalaryValueChange
        )}
        rowClassName={(record) => {
          const isAddingFromModal = servicesToShow.find(
            ({ Id }) => Id === Number(record.Key)
          )?.isAddingFromModal;

          if (
            isAddingFromModal ||
            addingFromModalServices.includes(Number(record.Key)) ||
            changedServices.includes(Number(record.Key))
          ) {
            return 'category-item adding-from-modal';
          }
          return 'category-item';
        }}
        scroll={{ x: 'max-content' }}
      />

      <div
        className="button-container"
        style={{ display: hasUserRightToCreateService ? 'flex' : 'none' }}
      >
        <Button
          type="default"
          onClick={() => {
            setModalOpen(true);
          }}
          data-testid="add-service"
        >
          {ADD_CLIENT_SERVICES_LABEL}
        </Button>
        <Button
          type="primary"
          onClick={saveData}
          disabled={isSavingBlocked}
          data-testid="save-all-services"
        >
          Сохранить
        </Button>
      </div>

      <Modal
        data-testid="add-service-popup"
        className="adding-service-modal"
        okText={generateSpanWithDataTestId(SAVE, 'save-was-added')}
        cancelText={generateSpanWithDataTestId(CANCEL, 'cancel-was-added')}
        title={generateSpanWithDataTestId(
          'Добавление услуги',
          'add-service-title'
        )}
        open={modalOpen}
        onCancel={() => {
          handleModalClose();
          setSearchQuery('');
        }}
        footer={
          processedModalServices.length > 0 ? (originNode) => originNode : null
        }
        onOk={handleOk}
        closeIcon={<CloseIconsForModalWithDataTestId />}
      >
        {(processedModalServices || []).length > 0 ? (
          <>
            <Input
              placeholder="Поиск услуги"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
              style={{ marginBottom: 12 }}
              data-testid="search-service-input"
              addonBefore={<SearchOutlined />}
            />
            <Collapse
              ghost
              expandIcon={({ isActive }) => (
                <RightOutlined
                  rotate={isActive ? 90 : 0}
                  data-testid="close-open-categories"
                />
              )}
              items={processedModalServices
                .filter((service) => {
                  const servicesSet = new Set(
                    servicesToShow.map((service) => service.Id)
                  );
                  const allExist = service.items.every((item) =>
                    servicesSet.has(item.Id)
                  );
                  return !allExist;
                })
                .filter((service) => {
                  if (!searchQuery.trim()) return true;
                  const query = searchQuery.trim().toLowerCase();
                  return (
                    service.name?.toLowerCase().includes(query) ||
                    service.items.some((item) =>
                      item.Name?.toLowerCase().includes(query)
                    )
                  );
                })
                .map((service, index) => {
                  return {
                    key: index,
                    label: (
                      <div
                        className="checkbox-container"
                        data-testid="category-item-was-added"
                      >
                        <Checkbox
                          style={{ marginRight: '14px' }}
                          onClick={(e) => {
                            e.stopPropagation();

                            serviceLabelClick(e, service);
                          }}
                          checked={service.selected}
                          data-testid="add-checkbox"
                        >
                          <Paragraph
                            type="secondary"
                            ellipsis={{ rows: 1 }}
                            className="checkbox-title"
                            data-testid="category-name-was-added"
                          >
                            {service.name}
                          </Paragraph>
                        </Checkbox>
                      </div>
                    ),
                    children: service.items
                      .filter(
                        ({ Id: serviceId }) =>
                          !servicesToShow.find(({ Id }) => serviceId === Id)
                      )
                      .filter((item) => {
                        if (!searchQuery.trim()) return true;
                        const query = searchQuery.trim().toLowerCase();
                        return (
                          item.Name?.toLowerCase().includes(query) ||
                          service.name?.toLowerCase().includes(query)
                        );
                      })
                      .map((item) => {
                        return (
                          <div key={item.Id}>
                            <div
                              className="client-modal-service"
                              key={item.Id}
                              style={{
                                marginBottom: '20px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginLeft: '20px',
                              }}
                            >
                              <div style={{ display: 'flex', gap: '16px' }}>
                                <Checkbox
                                  checked={item.Selected}
                                  onChange={(e) => {
                                    handleServiceModalCheckboxClick(
                                      e.target.checked,
                                      item.Id,
                                      service
                                    );
                                  }}
                                />
                                <div className="modal-cell">
                                  <Text data-testid="service-name-was-added">
                                    {item.Name}
                                  </Text>
                                </div>
                              </div>
                              <Text
                                type="secondary"
                                style={{
                                  maxWidth: '300px',
                                  minWidth: '70px',
                                  marginLeft: '10px',
                                  textAlign: 'end',
                                }}
                              >
                                {item.Duration ? (
                                  <>
                                    {`${transformMinutes(item.Duration)},`}
                                    <br />
                                  </>
                                ) : (
                                  ''
                                )}{' '}
                                <span data-testid="price-was-added">
                                  {item.PriceE && 'от'} {item.PriceS}{' '}
                                  {item.PriceE && `до ${item.PriceE}`}
                                </span>
                              </Text>
                            </div>
                          </div>
                        );
                      }),
                  };
                })}
            />
          </>
        ) : (
          <Empty
            description={
              <span>
                Услуги отсутствуют. Для привязки услуг исполнителю сначала
                создайте услуги.
              </span>
            }
          >
            <Button
              type="primary"
              onClick={() => navigate(`${ADD_SERVICE_PATH}?key=1`)}
            >
              Создать услугу
            </Button>
          </Empty>
        )}
      </Modal>
      <SetEmployeeSalaryModal
        setIsModalOpen={setEmployeeSalaryModalOpen}
        isModalOpen={employeeSalaryModalOpen}
        handleAddServicesSalaryChange={handleAddServicesSalaryChange}
      />
      {employee?.id && (
        <CopyEmployeeServicesModal
          isOpen={isCopyModalOpen}
          setIsOpen={setIsCopyModalOpen}
          employeeId={employee.id}
        />
      )}
    </div>
  );
};
